import React from "react";
import { useNavigate } from "react-router-dom";
import { Portrait } from "../../../../../../shared/frontend/ui";
import { QuickSearchDialog as SharedQuickSearchDialog } from "../../../../../../shared/frontend/search/QuickSearchDialog";
import type { SearchEntry, SearchSource } from "../../../../../../shared/frontend/search/searchContract";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";
import { useQeddyProvider } from "../../../../../../shared/frontend/help/QeddyProvider";
import { lifecycleLabel, lifecycleLabelKey } from "../../pages/persons/lifecycle";
import type { PersonDto } from "../../pages/persons/types";
import { useSettingsVisibilityContext } from "../../pages/settings/useSettingsVisibilityContext";
import { useAuth } from "../auth/useAuth";
import { Permissions } from "../constants/permissions";
import { useI18n } from "../i18n/useI18n";
import { buildPersonPin, routePinId, type PinnedLink } from "../pins/pinnedLinks";
import { usePinnedLinks } from "../pins/usePinnedLinks";
import { useGlobalStudioContext } from "../studios/useGlobalStudioContext";
import { sortPersonsByScore } from "../utils/personSearchScore";
import type { MenuRoute } from "./menu";
import { buildQuickSearchPages } from "./quickSearchPages";

const PERSON_LIMIT = 8;
/** Es werden mehr Personen geladen als angezeigt, damit die Bewertung die besten auswählen kann. */
const PERSON_FETCH_FACTOR = 2;
const RECENT_LIMIT = 5;
type RecentPerson = Pick<PersonDto, "id" | "firstName" | "lastName" | "lifecycleStatus" | "gender">;

const recentStorageKey = (accountId: string | undefined) => `m1member.quickSearch.recent.${accountId || "anon"}`;

// Zuletzt geöffnete Personen sind reiner Komfort je Browser: Fehlt der Speicher, bleibt die Liste leer.
function readRecent(accountId: string | undefined): RecentPerson[] {
  try {
    const raw = window.localStorage.getItem(recentStorageKey(accountId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as RecentPerson[]).filter((p) => p && typeof p.id === "string").slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
}

function writeRecent(accountId: string | undefined, person: RecentPerson) {
  try {
    const next = [person, ...readRecent(accountId).filter((p) => p.id !== person.id)].slice(0, RECENT_LIMIT);
    window.localStorage.setItem(recentStorageKey(accountId), JSON.stringify(next));
  } catch {
    // Ohne Browserspeicher entfällt nur die Liste der zuletzt geöffneten Personen.
  }
}

export type QuickSearchDialogProps = { open: boolean; onClose: () => void; menu: MenuRoute[] };

/** Product adapter: person API, studio scope, legacy recents and pins. The search session belongs to Shared. */
export function QuickSearchDialog({ open, onClose, menu }: QuickSearchDialogProps) {
  const auth = useAuth();
  const { text, lang } = useI18n();
  const nav = useNavigate();
  const feedback = useFeedback();
  const { appendStudioIdsQuery } = useGlobalStudioContext();
  const settingsVisibility = useSettingsVisibilityContext();
  const { isPinned, togglePin } = usePinnedLinks();
  const accountId = auth.info?.accountId;
  const recentScope = JSON.stringify([auth.info?.tenantId, accountId, appendStudioIdsQuery(""), auth.info?.studioAccessScope, auth.info?.allowedStudioIdsCsv, auth.info?.role]);
  const canSearchPersons = auth.hasAnyPermission(Permissions.PersonsRead);
  const qeddy = useQeddyProvider();
  const pin = (value: PinnedLink) => ({ active: () => isPinned(value.id), toggle: () => {
    if (!togglePin(value)) feedback.warning(text("Maximum number of pinned pages reached"));
  } });
  const personEntry = (person: RecentPerson): SearchEntry => ({
    key: `person:${person.id}`,
    label: `${person.firstName || ""} ${person.lastName || ""}`.trim() || text("(no name)"),
    description: lifecycleLabel(person.lifecycleStatus, person.gender, text),
    icon: <Portrait firstName={person.firstName} lastName={person.lastName} />,
    open: () => { writeRecent(recentScope, person); nav(`/persons/${encodeURIComponent(person.id)}`); },
    pin: pin(buildPersonPin({ personId: person.id, label: `${person.firstName || ""} ${person.lastName || ""}`.trim() || text("(no name)"), subtitleKey: lifecycleLabelKey(person.lifecycleStatus), hasPhoto: (person as Partial<PersonDto>).hasPhoto }))
  });
  const source: SearchSource | undefined = canSearchPersons ? {
    key: "persons",
    load: async (term, signal) => {
      const persons = await auth.api.get<PersonDto[]>(appendStudioIdsQuery(`/api/v1/persons?q=${encodeURIComponent(term)}&mode=extended&limit=${PERSON_LIMIT * PERSON_FETCH_FACTOR}`), { signal, suppressGlobalErrorToast: true });
      return [{ key: "persons", label: text("People"), entries: sortPersonsByScore(persons, term).slice(0, PERSON_LIMIT).map(personEntry) }];
    }
  } : undefined;
  const pages = React.useMemo(() => buildQuickSearchPages(menu, settingsVisibility, text), [menu, settingsVisibility, text]);
  return <SharedQuickSearchDialog open={open} onClose={onClose} language={lang}
    scopeKey={JSON.stringify([auth.info?.tenantId, accountId, appendStudioIdsQuery(""), auth.info?.permissions, auth.info?.allowedStudioIdsCsv, auth.info?.studioAccessScope, auth.info?.role, canSearchPersons, lang, pages])}
    labels={{ search: text("Search"), placeholder: text("Quick search placeholder"), pages: text("Pages"), empty: text("No results."), error: text("Error loading data"), pin: text("Pin"), unpin: text("Remove pin"), ask: text("Ask Qeddy") }}
    pages={pages} onPageOpen={page => nav(page.path)} pagePin={page => pin({ id: routePinId(page.path), kind: "route", path: page.path })}
    source={source} initialGroups={canSearchPersons ? [{ key: "recent", label: text("Recently opened"), entries: open ? readRecent(recentScope).map(personEntry) : [] }] : []}
    ask={qeddy?.launcher?.state === "ready" ? qeddy.ask : undefined} />;
}
